/**
 * What a device row actually says about a device, decided once for both screens that show one.
 *
 * The list and the detail page each need the same three judgments, and the console review found what
 * happens when a screen makes them inline: every device rendered as `active` with a recent *Last seen*,
 * whether or not it had ever completed a check-in. A machine that can receive a policy and one that has
 * never spoken to the feed looked identical, and the facts to tell them apart were already in the
 * browser — the API returns them and the table dropped them.
 *
 * Two traps are closed here rather than in the components.
 *
 * `lastSeenUtc` is stamped when telemetry arrives, not when a device checks in. That is why twenty-one
 * simulator devices looked healthy: they had been publishing events without ever polling the feed.
 * Only `lastCheckInUtc` says the device has talked to the management plane.
 *
 * `envelopeSchemaVersion` is `NOT NULL DEFAULT 1` server-side, so a device that has never checked in
 * carries a version it never reported. Rendering that number would state a server default as a device
 * report — the same defect as `last_seen_utc` standing in for a check-in, committed in the screen built
 * to expose it. So the version is keyed off the check-in, never off the number (D-263, D-270).
 */

export interface DeviceHealthSource {
  status: string
  /**
   * Absent, not null, when the device has never checked in.
   *
   * The API omits null properties, so this arrives as `undefined` — the declared `string | null`
   * describes the C# record, not the JSON. Every check below is `hasCheckedIn`, which treats the two
   * identically, because a strict `=== null` silently classified every never-checked-in device as
   * healthy and was only caught by looking at the rendered screen.
   */
  lastCheckInUtc?: string | null
  envelopeSchemaVersion: number
}

/** True only when the device has actually reported a check-in. Absent and null mean the same thing. */
export function hasCheckedIn(device: DeviceHealthSource): boolean {
  return device.lastCheckInUtc !== null && device.lastCheckInUtc !== undefined
}

export interface EnvelopeSchema {
  /** False when the device has never checked in, whatever the payload's number says. */
  reported: boolean
  version: number | null
  label: string
}

export function envelopeSchema(device: DeviceHealthSource): EnvelopeSchema {
  if (!hasCheckedIn(device)) return { reported: false, version: null, label: 'not reported' }

  switch (device.envelopeSchemaVersion) {
    case 2:
      return { reported: true, version: 2, label: '2 — hybrid envelope, any policy size' }
    case 1:
      return { reported: true, version: 1, label: '1 — direct RSA, small policies only' }
    default:
      return {
        reported: true,
        version: device.envelopeSchemaVersion,
        label: String(device.envelopeSchemaVersion)
      }
  }
}

/** What the check-in column says. A device that has never checked in says so in words, not with a dash. */
export function checkInState(device: DeviceHealthSource): 'never' | 'checked-in' {
  return hasCheckedIn(device) ? 'checked-in' : 'never'
}

/**
 * The three states of "what is this device running", which is not the same question twice.
 *
 * A device that has never checked in has never *said* what it is running, which is a different fact
 * from one that checked in and reported nothing. Collapsing them into an empty table would be the
 * absence rule broken again.
 */
export function activePoliciesAbsence(
  device: DeviceHealthSource & { activePolicies: unknown[] }
): string | null {
  if (!hasCheckedIn(device)) {
    return 'This device has not checked in, so it has never said what it is running.'
  }

  if (device.activePolicies.length === 0) {
    return 'This device checked in and reported no policy running.'
  }

  return null
}

/** The reason codes the server returns, as sentences an operator can act on. */
const WITHHELD_REASONS: Record<string, string> = {
  'schema-not-reported':
    'This device has never checked in, so it has never reported which envelope format it can read. Nothing can be predicted until it does.',
  'unsupported-schema-version':
    'This device reports an envelope format older than anything this deployment produces, so it is served nothing rather than something it cannot open.',
  'revoked-on-version-1':
    'This policy was withdrawn, and the version 1 envelope has no way to say so. The device is told by the policy leaving its feed.',
  'no-signing-key':
    'This organization has no active signing key, so nothing can be signed for any device. Rotate one on the Trust screen.',
  'no-device-encryption-key':
    'This device has registered no encryption key, so no version 2 envelope can be sealed for it. It registers one at enrollment or check-in.'
}

export function describeWithholding(reason: string | null): string {
  if (reason === null) return 'Withheld for a reason this console does not recognize.'
  return WITHHELD_REASONS[reason] ?? `Withheld: ${reason}.`
}

/**
 * What the row's one prominent badge should say, which is not the same question as `status`.
 *
 * `status` is the enrollment state - active, retired, revoked - and it is the only thing the fleet
 * grid used to show. So nine hundred and sixty-seven machines that had never once spoken to the
 * management plane were each painted with a green `active` pill beside the word "never" in the
 * check-in column, and the badge is the thing an operator's eye actually lands on. The detail page
 * committed the same contradiction more plainly: a warning saying "anything published to it is
 * waiting", under a green badge.
 *
 * So the badge answers "can this machine be managed", and the enrollment status is shown as the fact
 * it is rather than as a verdict. A device that has never checked in is `unreached` whatever its
 * status says, because nothing published can get to it. The severity class is `sev-3` there, not
 * `sev-5`: it is a gap to close, not an incident, and the fleet a customer is part-way through
 * rolling out is mostly this (D-431).
 */
export type DeviceState = 'unreached' | 'reached' | 'retired'

export interface DeviceStateDescription {
  state: DeviceState
  /** The badge word. Never a color on its own - Broadsheet has two inks (D-333). */
  label: string
  /** The `pill` modifier that paints it. */
  pill: string
  /** One clause saying what the badge means, for the places that have room for it. */
  meaning: string
}

export function deviceState(device: DeviceHealthSource): DeviceStateDescription {
  if (device.status !== 'active') {
    return {
      state: 'retired',
      label: device.status,
      pill: 'pill',
      meaning: 'This device is out of the fleet. It is served an empty feed and holds no seat.'
    }
  }

  if (!hasCheckedIn(device)) {
    return {
      state: 'unreached',
      label: 'never checked in',
      pill: 'pill warn',
      meaning: 'Enrolled, and has never collected a policy. Anything published to it is waiting.'
    }
  }

  return {
    state: 'reached',
    label: 'checked in',
    pill: 'pill sev-2',
    meaning: 'This device has collected a policy from the feed at least once.'
  }
}
